import { useEffect, useMemo, useState } from "react";

const ACHIEVEMENTS_SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1-B3reA5h2nkQOg3RlrHXhtrnJ_TNfzdhQOt26llfi8I/gviz/tq?tqx=out:csv&sheet=Achievements";

/* =========================
   CLOUD STYLES & ANIMATIONS
========================= */
const cloudStyles = `
  @keyframes floatCloudLeft {
    0% { transform: translateX(0px); }
    50% { transform: translateX(15px); }
    100% { transform: translateX(0px); }
  }
  @keyframes floatCloudRight {
    0% { transform: translateX(0px); }
    50% { transform: translateX(-15px); }
    100% { transform: translateX(0px); }
  }

  /* FLUFFY CLOUD BASE */
  .cloud-shape {
    position: absolute;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 50px;
    pointer-events: none;
    filter: drop-shadow(0 6px 12px rgba(255, 255, 255, 0.6));
    z-index: 0;
  }

  /* TOP PUFFS */
  .cloud-shape::before,
  .cloud-shape::after {
    content: '';
    position: absolute;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 50%;
  }

  .cloud-shape::before {
    width: 50%;
    height: 110%;
    top: -45%;
    left: 15%;
  }

  .cloud-shape::after {
    width: 42%;
    height: 90%;
    top: -35%;
    right: 15%;
  }

  /* POSITIONS & SIZES */
  .cloud1 {
    left: -20px;
    top: 80px;
    width: 170px;
    height: 50px;
    animation: floatCloudLeft 8s ease-in-out infinite;
  }

  .cloud2 {
    right: -25px;
    top: 180px;
    width: 190px;
    height: 55px;
    animation: floatCloudRight 9s ease-in-out infinite;
  }

  .cloud3 {
    left: -30px;
    top: 520px;
    width: 160px;
    height: 48px;
    animation: floatCloudLeft 10s ease-in-out infinite;
  }

  .cloud4 {
    right: -30px;
    top: 780px;
    width: 180px;
    height: 52px;
    animation: floatCloudRight 11s ease-in-out infinite;
  }
`;

/*
  ==========================================
  ACHIEVEMENT THRESHOLDS
  ==========================================
*/

const ACHIEVEMENT_THRESHOLDS = {
  streak7: 7,
  streak30: 30,
  streak100: 100,
  streak365: 365,

  aroundWorld: 24901,
  moon: 238855,
  millionaire: 1000000,

  countries10: 10,
  countries25: 25,
  countries50: 50,
  countries188: 188,

  games100: 100,

  hours24: 24,
  hours100: 100,
  hours500: 500,
  hours1000: 1000,
};

/*
  ==========================================
  CSV PARSER & HELPERS
  ==========================================
*/

function parseCSV(text) {
  const rows = [];
  let row = [];
  let value = "";
  let insideQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"' && insideQuotes && next === '"') {
      value += '"';
      i++;
      continue;
    }

    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === "," && !insideQuotes) {
      row.push(value);
      value = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !insideQuotes) {
      if (char === "\r" && next === "\n") {
        i++;
      }

      row.push(value);
      value = "";

      if (row.some((cell) => cell.trim() !== "")) {
        rows.push(row);
      }

      row = [];
      continue;
    }

    value += char;
  }

  if (value !== "" || row.length > 0) {
    row.push(value);

    if (row.some((cell) => cell.trim() !== "")) {
      rows.push(row);
    }
  }

  return rows;
}

function clean(value) {
  return String(value ?? "").trim();
}

function numberValue(value) {
  if (value === null || value === undefined) {
    return 0;
  }

  const cleaned = String(value)
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "");

  const result = Number(cleaned);

  return Number.isFinite(result) ? result : 0;
}

function datesMatch(d1, d2) {
  if (!d1 || !d2) return false;
  const str1 = clean(d1).toLowerCase();
  const str2 = clean(d2).toLowerCase();
  if (str1 === str2) return true;

  const date1 = new Date(str1);
  const date2 = new Date(str2);
  if (!isNaN(date1.getTime()) && !isNaN(date2.getTime())) {
    return (
      date1.toISOString().split("T")[0] ===
      date2.toISOString().split("T")[0]
    );
  }
  return false;
}

/*
  ==========================================
  LOCAL STORAGE & STREAKS
  ==========================================
*/

function getHistory() {
  try {
    const history = JSON.parse(localStorage.getItem("guessHistory") || "[]");
    return Array.isArray(history) ? history : [];
  } catch (error) {
    console.error("Could not read guess history:", error);
    return [];
  }
}

function getCurrentStreak() {
  return Number(localStorage.getItem("streak") || 0);
}

function calculateLongestStreak(history) {
  if (!history.length) return 0;

  const successfulDates = history
    .filter((entry) => entry?.success)
    .map((entry) => entry.date)
    .filter(Boolean)
    .sort();

  if (!successfulDates.length) return 0;

  let longest = 1;
  let current = 1;

  for (let i = 1; i < successfulDates.length; i++) {
    const previous = new Date(successfulDates[i - 1]);
    const currentDate = new Date(successfulDates[i]);

    const difference = Math.round(
      Math.abs(currentDate - previous) / (1000 * 60 * 60 * 24)
    );

    if (difference === 1) {
      current++;
      longest = Math.max(longest, current);
    } else if (difference > 1) {
      current = 1;
    }
  }

  return longest;
}

/*
  ==========================================
  MAIN COMPONENT
  ==========================================
*/

export default function StatsPage({ onBack }) {
  const [history] = useState(() => getHistory());
  const [currentStreak] = useState(() => getCurrentStreak());
  const [achievementRows, setAchievementRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetError, setSheetError] = useState(false);

  useEffect(() => {
    async function loadAchievements() {
      try {
        const response = await fetch(
          `${ACHIEVEMENTS_SHEET_URL}&t=${Date.now()}`,
          { cache: "no-store" }
        );

        if (!response.ok) {
          throw new Error("Could not load Achievements sheet");
        }

        const text = await response.text();
        const rows = parseCSV(text);

        if (!rows.length) {
          throw new Error("Achievements sheet is empty");
        }

        const headers = rows[0].map((header) => clean(header).toLowerCase());

        const data = rows.slice(1).map((row) => {
          const object = {};
          headers.forEach((header, index) => {
            object[header] = clean(row[index]);
          });
          return object;
        });

        setAchievementRows(data);
      } catch (error) {
        console.error("Failed to load Achievements sheet:", error);
        setSheetError(true);
      } finally {
        setLoading(false);
      }
    }

    loadAchievements();
  }, []);

  /* PLAYER STATS */
  const totalGames = history.length;
  const wins = history.filter((entry) => entry?.success).length;
  const hasOneGuessWin = history.some(
    (entry) => entry?.success && Number(entry?.guesses) === 1
  );
  const winRate = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;
  const averageGuesses =
    totalGames > 0
      ? (
          history.reduce(
            (total, entry) => total + Number(entry?.guesses || 0),
            0
          ) / totalGames
        ).toFixed(1)
      : "0.0";

  const longestStreak = calculateLongestStreak(history);

  /* AGGREGATED STATS FROM GUESS HISTORY + GOOGLE SHEET */
  const journeyStats = useMemo(() => {
    if (!history.length || !achievementRows.length) {
      return { totalMiles: 0, hours: 0, countries: 0, continents: 0 };
    }

    let totalMiles = 0;
    let hours = 0;
    const uniqueCountries = new Set();
    const uniqueContinents = new Set();

    history.forEach((entry) => {
      if (!entry?.date) return;

      const row = achievementRows.find((r) => {
        const rowDate = r.date || r["date"];
        return datesMatch(rowDate, entry.date);
      });

      if (row) {
        // Miles & Hours (summed)
        const m = numberValue(row["miles"]);
        const h = numberValue(row["flight time"] || row["flighttime"] || row["flight_time"]);
        totalMiles += m;
        hours += h;

        // Countries (unique count)
        const rawCountries = row["country"] || row["countries"];
        if (
          rawCountries !== undefined &&
          rawCountries !== null &&
          rawCountries !== ""
        ) {
          const countryList = String(rawCountries)
            .split(/[,|;\n]+/)
            .map((c) => c.trim().toLowerCase())
            .filter(Boolean);

          countryList.forEach((c) => uniqueCountries.add(c));
        }

        // Continents (unique count)
        const rawContinents = row["continents"] ?? row["continent"];
        if (
          rawContinents !== undefined &&
          rawContinents !== null &&
          rawContinents !== ""
        ) {
          const continentList = String(rawContinents)
            .split(/[,|;\n]+/)
            .map((c) => c.trim().toLowerCase())
            .filter(Boolean);

          continentList.forEach((c) => uniqueContinents.add(c));
        }
      }
    });

    return {
      totalMiles,
      hours,
      countries: uniqueCountries.size,
      continents: uniqueContinents.size,
    };
  }, [history, achievementRows]);

  const { totalMiles, hours, countries, continents } = journeyStats;

  /* UPDATED ACHIEVEMENTS LIST */
  const achievements = [
    {
      id: "first-flight",
      icon: "🛫",
      title: "First Flight",
      description: "Complete 1 Flightle",
      unlocked: totalGames >= 1,
      progress: totalGames,
      target: 1,
    },
    {
      id: "frequent-flyer",
      icon: "🎟️",
      title: "Frequent Flyer",
      description: "Complete 100 Flightles",
      unlocked: totalGames >= ACHIEVEMENT_THRESHOLDS.games100,
      progress: totalGames,
      target: ACHIEVEMENT_THRESHOLDS.games100,
    },
    {
      id: "weekender",
      icon: "🔥",
      title: "Weekender",
      description: "7-day streak",
      unlocked: longestStreak >= ACHIEVEMENT_THRESHOLDS.streak7,
      progress: longestStreak,
      target: ACHIEVEMENT_THRESHOLDS.streak7,
    },
    {
      id: "monthly-flyer",
      icon: "🗓️",
      title: "Monthly Flyer",
      description: "30-day streak",
      unlocked: longestStreak >= ACHIEVEMENT_THRESHOLDS.streak30,
      progress: longestStreak,
      target: ACHIEVEMENT_THRESHOLDS.streak30,
    },
    {
      id: "century-flyer",
      icon: "💯",
      title: "Century Flyer",
      description: "100-day streak",
      unlocked: longestStreak >= ACHIEVEMENT_THRESHOLDS.streak100,
      progress: longestStreak,
      target: ACHIEVEMENT_THRESHOLDS.streak100,
    },
    {
      id: "year-in-air",
      icon: "🎆",
      title: "Year in the Air",
      description: "365-day streak",
      unlocked: longestStreak >= ACHIEVEMENT_THRESHOLDS.streak365,
      progress: longestStreak,
      target: ACHIEVEMENT_THRESHOLDS.streak365,
    },
    {
      id: "around-world",
      icon: "🔄",
      title: "Around the World",
      description: "Fly 24,901 miles total",
      unlocked: totalMiles >= ACHIEVEMENT_THRESHOLDS.aroundWorld,
      progress: totalMiles,
      target: ACHIEVEMENT_THRESHOLDS.aroundWorld,
    },
    {
      id: "moon",
      icon: "🌙",
      title: "To the Moon",
      description: "Fly 238,855 miles total",
      unlocked: totalMiles >= ACHIEVEMENT_THRESHOLDS.moon,
      progress: totalMiles,
      target: ACHIEVEMENT_THRESHOLDS.moon,
    },
    {
      id: "millionaire",
      icon: "💸",
      title: "Millionaire",
      description: "Fly 1,000,000 miles total",
      unlocked: totalMiles >= ACHIEVEMENT_THRESHOLDS.millionaire,
      progress: totalMiles,
      target: ACHIEVEMENT_THRESHOLDS.millionaire,
    },
    {
      id: "six-continents",
      icon: "🌍",
      title: "Six Continents",
      description: "Visit all 6 inhabited continents",
      unlocked: continents >= 6,
      progress: continents,
      target: 6,
    },
    {
      id: "explorer",
      icon: "🧭",
      title: "Explorer",
      description: "Visit 10 countries",
      unlocked: countries >= ACHIEVEMENT_THRESHOLDS.countries10,
      progress: countries,
      target: ACHIEVEMENT_THRESHOLDS.countries10,
    },
    {
      id: "globetrotter",
      icon: "🗺️",
      title: "Globetrotter",
      description: "Visit 25 countries",
      unlocked: countries >= ACHIEVEMENT_THRESHOLDS.countries25,
      progress: countries,
      target: ACHIEVEMENT_THRESHOLDS.countries25,
    },
    {
      id: "world-traveller",
      icon: "🌐",
      title: "World Traveller",
      description: "Visit 50 countries",
      unlocked: countries >= ACHIEVEMENT_THRESHOLDS.countries50,
      progress: countries,
      target: ACHIEVEMENT_THRESHOLDS.countries50,
    },
    {
      id: "all-countries",
      icon: "👑",
      title: "Global Master",
      description: "Visit all 188 countries with an international airport",
      unlocked: countries >= ACHIEVEMENT_THRESHOLDS.countries188,
      progress: countries,
      target: ACHIEVEMENT_THRESHOLDS.countries188,
    },
    {
      id: "around-the-clock",
      icon: "⏱️",
      title: "Flight Time",
      description: "24 hours total in the air",
      unlocked: hours >= ACHIEVEMENT_THRESHOLDS.hours24,
      progress: hours,
      target: ACHIEVEMENT_THRESHOLDS.hours24,
    },
    {
      id: "sky-cruiser",
      icon: "✈️",
      title: "Sky Cruiser",
      description: "100 hours total in the air",
      unlocked: hours >= ACHIEVEMENT_THRESHOLDS.hours100,
      progress: hours,
      target: ACHIEVEMENT_THRESHOLDS.hours100,
    },
    {
      id: "sky-captain",
      icon: "👨‍✈️",
      title: "Sky Captain",
      description: "500 hours total in the air",
      unlocked: hours >= ACHIEVEMENT_THRESHOLDS.hours500,
      progress: hours,
      target: ACHIEVEMENT_THRESHOLDS.hours500,
    },
    {
      id: "lucky-guess",
      icon: "🍀",
      title: "Lucky guess?!",
      description: "Complete a Flightle with only 1 guess",
      unlocked: hasOneGuessWin,
      progress: hasOneGuessWin ? 1 : 0,
      target: 1,
    },
  ];

  function formatNumber(value) {
    return Number(value || 0).toLocaleString();
  }

  function formatMiles(value) {
    return `${formatNumber(value)} miles`;
  }

  const unlockedCount = achievements.filter((a) => a.unlocked).length;

  return (
    <div style={styles.page}>
      <style>{cloudStyles}</style>

      {/* CLOUDS VISIBLE IN BACKGROUND */}
      <div className="cloud-shape cloud1"></div>
      <div className="cloud-shape cloud2"></div>
      <div className="cloud-shape cloud3"></div>
      <div className="cloud-shape cloud4"></div>

      <div style={styles.container}>
        {/* HEADER */}
        <header style={styles.header}>
          <button onClick={onBack} style={styles.backButton} aria-label="Back">
            ←
          </button>
          <h1 style={styles.title}>Flight Log</h1>
          <div style={styles.headerSpacer} />
        </header>

        {/* HERO STREAK CARD */}
        <section style={styles.heroCard}>
          <div style={styles.heroBadge}>CURRENT STREAK</div>
          <div style={styles.heroStreakRow}>
            <span style={styles.heroEmoji}>🔥</span>
            <span style={styles.heroNumber}>{currentStreak}</span>
            <span style={styles.heroUnit}>DAYS</span>
          </div>

          <div style={styles.heroMotivation}>
            {currentStreak > 0
              ? "You're on fire! Keep flying every day!"
              : "Play today's Flightle to start a new streak!"}
          </div>

          {achievementRows.length > 0 && (
            <div style={styles.heroSubtext}>
              ✈️ <strong>{formatMiles(totalMiles)}</strong> covered on your journey
            </div>
          )}
        </section>

        {/* BASIC STATS GRID */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Pilot Statistics</h2>

          <div style={styles.statsGrid}>
            <StatCard
              icon="✈️"
              value={formatNumber(totalGames)}
              label="Flightles played"
              color="#0d9488"
            />
            <StatCard
              icon="🏆"
              value={formatNumber(wins)}
              label="Victories"
              color="#16a34a"
            />
            <StatCard
              icon="🎯"
              value={`${winRate}%`}
              label="Win rate"
              color="#0284c7"
            />
            <StatCard
              icon="📊"
              value={averageGuesses}
              label="Average guesses"
              color="#ea580c"
            />
          </div>
        </section>

        {/* TRAVEL STATS */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Your Journey Log</h2>
          </div>

          <div style={styles.travelCard}>
            <TravelRow
              icon="🌎"
              label="Miles travelled"
              value={achievementRows.length ? formatMiles(totalMiles) : "—"}
            />
            <TravelRow
              icon="🗺️"
              label="Countries visited"
              value={achievementRows.length ? formatNumber(countries) : "—"}
            />
            <TravelRow
              icon="🌍"
              label="Continents visited"
              value={achievementRows.length ? `${continents} / 6` : "—"}
            />
            <TravelRow
              icon="⏱️"
              label="Time in the air"
              value={
                achievementRows.length ? `${formatNumber(hours)} hours` : "—"
              }
            />
          </div>

          {loading && (
            <div style={styles.loading}>
              <span style={styles.spinner}>✈️</span> Syncing passport data...
            </div>
          )}

          {sheetError && (
            <div style={styles.error}>
              Could not load live journey data. Please make sure your sheet is public.
            </div>
          )}
        </section>

        {/* ACHIEVEMENTS GRID (2-COLUMNS) */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Achievements</h2>
            <span style={styles.badgeCount}>
              {unlockedCount} / {achievements.length} Unlocked
            </span>
          </div>

          <div style={styles.achievementsGrid}>
            {achievements.map((achievement) => (
              <AchievementCard
                key={achievement.id}
                achievement={achievement}
                formatNumber={formatNumber}
              />
            ))}
          </div>
        </section>

        <footer style={styles.footer}>
          Clear skies ahead. Keep soaring! ✈️
        </footer>
      </div>
    </div>
  );
}

/*
  ==========================================
  SUB-COMPONENTS
  ==========================================
*/

function StatCard({ icon, value, label, color }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statTop}>
        <span style={styles.statIcon}>{icon}</span>
        <span style={{ ...styles.statValue, color: color || "#0f172a" }}>
          {value}
        </span>
      </div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

function TravelRow({ icon, label, value }) {
  return (
    <div style={styles.travelRow}>
      <div style={styles.travelLeft}>
        <span style={styles.travelIcon}>{icon}</span>
        <span style={styles.travelLabel}>{label}</span>
      </div>
      <strong style={styles.travelValue}>{value}</strong>
    </div>
  );
}

function AchievementCard({ achievement, formatNumber }) {
  const { unlocked, progress, target, icon, title, description } = achievement;

  let percentage = 0;
  if (target) {
    percentage = Math.min(
      100,
      Math.round(((progress || 0) / target) * 100)
    );
  }

  return (
    <div
      style={{
        ...styles.achievementCard,
        ...(unlocked ? styles.achievementUnlocked : styles.achievementLocked),
      }}
    >
      <div style={styles.achievementHeader}>
        <div
          style={{
            ...styles.achievementIconBg,
            background: unlocked ? "#fef3c7" : "#f1f5f9",
          }}
        >
          <span
            style={{
              filter: unlocked ? "none" : "grayscale(1)",
              opacity: unlocked ? 1 : 0.6,
            }}
          >
            {icon}
          </span>
        </div>
        {unlocked && <div style={styles.checkmarkBadge}>✓</div>}
      </div>

      <div style={styles.achievementTitle}>{title}</div>
      <div style={styles.achievementDesc}>{description}</div>

      {!unlocked && target > 1 && (
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${percentage}%`,
              }}
            />
          </div>
          <div style={styles.progressText}>
            {formatNumber(Math.min(progress || 0, target))} / {formatNumber(target)}
          </div>
        </div>
      )}

      {unlocked && <div style={styles.unlockedLabel}>UNLOCKED</div>}
    </div>
  );
}

/*
  ==========================================
  STYLES
  ==========================================
*/

const styles = {
  page: {
    minHeight: "100dvh",
    background: `linear-gradient(to bottom, #deecfc 0%, #dff3ff 45%, #eef7ff 100%)`,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    padding: "20px 16px 60px",
    boxSizing: "border-box",
    position: "relative",
    overflowX: "hidden",
    overflowY: "auto",
  },

  container: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },

  backButton: {
    border: "none",
    width: 40,
    height: 40,
    borderRadius: "50%",
    fontSize: 20,
    fontWeight: "bold",
    cursor: "pointer",
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent"
  },

  headerSpacer: {
    width: 40,
  },

  title: {
    fontSize: 22,
    margin: 0,
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "0.5px",
  },

  /* HERO CARD */
  heroCard: {
    background: "#ffffff",
    borderRadius: 20,
    padding: "24px 20px 20px",
    textAlign: "center",
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
    marginBottom: 22,
    position: "relative",
    border: "1px solid #e2e8f0",
  },

  heroBadge: {
    display: "inline-block",
    background: "#fef3c7",
    color: "#b45309",
    fontWeight: 800,
    fontSize: 11,
    padding: "4px 12px",
    borderRadius: 20,
    letterSpacing: "1px",
    marginBottom: 6,
  },

  heroStreakRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center",
    gap: 8,
  },

  heroEmoji: {
    fontSize: 36,
  },

  heroNumber: {
    fontSize: 56,
    lineHeight: 1,
    fontWeight: 900,
    color: "#0f172a",
  },

  heroUnit: {
    fontSize: 14,
    fontWeight: 800,
    color: "#b45309",
    letterSpacing: "1px",
  },

  heroMotivation: {
    fontSize: 14,
    fontWeight: 600,
    color: "#475569",
    marginTop: 6,
  },

  heroSubtext: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: "1px dashed #e2e8f0",
    fontSize: 13,
    color: "#334155",
  },

  /* SECTIONS */
  section: {
    marginBottom: 24,
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },

  sectionTitle: {
    fontSize: 18,
    margin: "0 0 12px",
    fontWeight: 800,
    color: "#0f172a",
  },

  badgeCount: {
    fontSize: 12,
    fontWeight: 700,
    color: "#0369a1",
    background: "#ffffff",
    padding: "4px 10px",
    borderRadius: 12,
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  },

  /* STATS GRID */
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 10,
  },

  statCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    padding: "14px 16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },

  statTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  statIcon: {
    fontSize: 20,
  },

  statValue: {
    fontSize: 22,
    fontWeight: 900,
  },

  statLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    marginTop: 6,
  },

  /* TRAVEL CARD */
  travelCard: {
    background: "#ffffff",
    borderRadius: 20,
    padding: "6px 18px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
  },

  travelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "13px 0",
    borderBottom: "1px solid #f1f5f9",
  },

  travelLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  travelIcon: {
    fontSize: 20,
  },

  travelLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#334155",
  },

  travelValue: {
    fontSize: 14,
    fontWeight: 800,
    color: "#0284c7",
  },

  loading: {
    textAlign: "center",
    color: "#0284c7",
    fontSize: 13,
    fontWeight: 600,
    marginTop: 12,
  },

  spinner: {
    display: "inline-block",
  },

  error: {
    background: "#fef2f2",
    borderRadius: 12,
    padding: 12,
    marginTop: 10,
    color: "#991b1b",
    fontSize: 12,
    fontWeight: 600,
    textAlign: "center",
  },

  /* ACHIEVEMENTS GRID (2-COL) */
  achievementsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 12,
  },

  achievementCard: {
    borderRadius: 18,
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxSizing: "border-box",
    position: "relative",
  },

  achievementUnlocked: {
    background: "#ffffff",
    border: "1px solid #fcd34d",
    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
  },

  achievementLocked: {
    background: "rgba(255, 255, 255, 0.85)",
    border: "1px solid #e2e8f0",
  },

  achievementHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },

  achievementIconBg: {
    width: 38,
    height: 38,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
  },

  checkmarkBadge: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "#16a34a",
    color: "white",
    fontSize: 12,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  achievementTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1.2,
  },

  achievementDesc: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 3,
    lineHeight: 1.3,
  },

  progressContainer: {
    marginTop: 10,
  },

  progressBar: {
    height: 6,
    background: "#e2e8f0",
    borderRadius: 10,
    overflow: "hidden",
  },

  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #38bdf8, #0284c7)",
    borderRadius: 10,
  },

  progressText: {
    fontSize: 10,
    fontWeight: 700,
    color: "#64748b",
    marginTop: 4,
    textAlign: "right",
  },

  unlockedLabel: {
    fontSize: 10,
    fontWeight: 800,
    color: "#b45309",
    marginTop: 10,
    letterSpacing: "0.5px",
  },

  footer: {
    textAlign: "center",
    color: "#0369a1",
    fontSize: 13,
    fontWeight: 700,
    marginTop: 20,
  },
};